import React, { Fragment, useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import AppButton from "../../Common/Button/Button";
import FormInput from "../../Common/FormInputs/MuiTextField";
import customConfig from "../../../config";
import { selectMenuProps } from "../../../utils/selectMenuProps";
import { convertCapabilityName, isJsonParseSuccessful, isJsonStringifySuccessful, validateJSONData } from "../../../utils/general";
import { useTheme } from "@mui/material/styles";
import { useDesignTokens } from "../../../theme/useDesignTokens";
import Grid2 from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";

const selectInputSx = { height: 45, minHeight: 45, width: "100%", "& .MuiOutlinedInput-notchedOutline": { border: "1px solid rgba(0, 0, 0, 0.42) !important" } };
const capabilityCardSx = { marginBottom: "15px", backgroundColor: "#f2f2f2" };
const attributeKeySx = { marginRight: { xs: 0, sm: '20px' } };
const attributeValuesSx = { width: "100%", minWidth: 0, overflow: "visible" };
const deleteCapabilityCardButtonSx = { float: "right" };
const customValueKeySx = { marginLeft: { xs: "0 !important", sm: "20px !important" } };

const CUSTOM_JSON_CONFIG = {
  label: "Custom Capability JSON",
  placeholder: 'e.g., {"capability": "PowerfulMode","attributes": [{"key": "acpm"}]}',
  rows: 10,
  helpText: "Valid JSON format is required",
};

const CONFIG = {
  customJson: {
    label: "Custom Capability JSON",
    placeholder: `e.g., {"capability": "PowerfulMode","attributes": [{"key": "acpm"}]} `,
    rows: 4,
    helpText: "Valid JSON format is required",
  },
};

const DeviceModelControlCapability = ({
    productCategory,
    isDeviceModelAddedUsingFileUpload,
    editDeviceModel,
    deviceModelData,
    triggerAddNewDeviceModel,
    addDeviceControlCapabilties,
    ModelData: { deviceModelsMetadata },
    channelAndSwitchDeviceTypes,
    areAllCustomJsonValidCheck
}) => {
    const theme = useTheme();
    const { colors } = useDesignTokens();
    const primaryColor = colors?.primary?.main || theme.palette.primary.main;
    const inputFieldHeight = 45;
    const focusOutlineSx = {
        "& .MuiOutlinedInput-root": { height: inputFieldHeight, minHeight: inputFieldHeight },
        "& .MuiInputBase-input": { height: `${inputFieldHeight}px !important`, minHeight: `${inputFieldHeight}px !important`, boxSizing: "border-box" },
        "& .MuiOutlinedInput-notchedOutline": { border: "1px solid rgba(0, 0, 0, 0.42) !important" },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0, 0, 0, 0.87)" },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: `${primaryColor} !important`, borderWidth: "1px",
        },
        "& .MuiOutlinedInput-root.Mui-expanded .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.42) !important", borderWidth: "1px",
        },
        "& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.Mui-expanded": { color: `${primaryColor} !important` },
    };

    const customCapabilityName = "CustomCapability";
    /** Next free name CustomCapability1, CustomCapability2, … based on list + attribute keys (edit load resets counter implicitly). */
    const computeNextCustomCapabilityInstanceName = (listKeys, attributeKeys) => {
        const keys = new Set([...(listKeys ?? []), ...(attributeKeys ?? [])]);
        let max = 0;
        for (const name of keys) {
            if (typeof name !== "string" || !name.startsWith(customCapabilityName)) continue;
            const suffix = name.slice(customCapabilityName.length);
            if (suffix === "") continue;
            const n = parseInt(suffix, 10);
            if (!Number.isNaN(n) && String(n) === suffix) {
                max = Math.max(max, n);
            }
        }
        let candidate = `${customCapabilityName}${max + 1}`;
        while (keys.has(candidate)) {
            max += 1;
            candidate = `${customCapabilityName}${max + 1}`;
        }
        return candidate;
    };

    const dedupeCapabilityNamesPreserveOrder = (names) => {
        const seen = new Set();
        const out = [];
        for (const n of names ?? []) {
            if (seen.has(n)) continue;
            seen.add(n);
            out.push(n);
        }
        return out;
    };
    const [controlCapabiltiesList, setControlCapabiltiesList] = useState(() => {
        const capabilities = Object.keys(
            deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities ?? {}
        ).sort();
        return Array.from(new Set([customCapabilityName, ...capabilities]));
    });
    const [selectedControlCapability, setSelectedControlCapability] = useState(() => {
        const capabilities = Object.keys(
            deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities ?? {}
        ).sort();
        const list = Array.from(new Set([customCapabilityName, ...capabilities]));
        return list?.[0] ?? "";
    });
    const [addedControlCapabilities, setAddedControlCapabilities] = useState([]);
    const [controlCapabilitiesAttributes, setControlCapabilitiesAttributes] = useState({});
    const [controlCapabilitySelectedValueType, setControlCapabilitySelectedValueType] = useState({});
    
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    /** Submit effect only depends on trigger; keep latest state here to avoid stale closures. */
    const addedControlCapabilitiesRef = useRef(addedControlCapabilities);
    const controlCapabilitiesAttributesRef = useRef(controlCapabilitiesAttributes);
    const errorsRef = useRef(errors);
    /** Custom/JSON rows added this session (Add Custom); stay on top until Update clears this. */
    const draftCustomCapabilityNamesRef = useRef(new Set());
    addedControlCapabilitiesRef.current = addedControlCapabilities;
    controlCapabilitiesAttributesRef.current = controlCapabilitiesAttributes;
    errorsRef.current = errors;

    const areAllCustomJsonValid = (errorsObj) => {
        return Object.values(errorsObj).every(error => error === "");
    };

    const isCustomCapability = (value) => value.startsWith(customCapabilityName);

    /** Same classification as the JSON branch in getControlCapabilityData (metadata-backed vs custom/JSON). */
    const isJsonOrCustomCapability = (capabilityName) =>
        isCustomCapability(capabilityName) ||
        !(
            deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities &&
            capabilityName in deviceModelsMetadata.categories[productCategory].controlCapabilities
        );

    const partitionControlCapabilities = (names) => {
        const defaults = [];
        const customs = [];
        for (const name of names) {
            if (isJsonOrCustomCapability(name)) customs.push(name);
            else defaults.push(name);
        }
        return { defaults, customs };
    };

    /** Saved / after Update / API load: metadata first, then all custom/JSON. */
    const orderControlCapabilitiesForPayload = (names) => {
        const { defaults, customs } = partitionControlCapabilities(names);
        return [...defaults, ...customs];
    };

    /** While editing: session-added custom/JSON on top, defaults middle, saved/other custom JSON at bottom. */
    const orderCapabilitiesWhileEditing = (names) => {
        const drafts = draftCustomCapabilityNamesRef.current;
        const list = dedupeCapabilityNamesPreserveOrder(names);
        const draftOrdered = [];
        const defaults = [];
        const otherCustoms = [];
        for (const name of list) {
            if (isJsonOrCustomCapability(name) && drafts.has(name)) {
                draftOrdered.push(name);
            } else if (isJsonOrCustomCapability(name)) {
                otherCustoms.push(name);
            } else {
                defaults.push(name);
            }
        }
        return [...draftOrdered, ...defaults, ...otherCustoms];
    };

    useEffect(() => {
        if (deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities ?? null) {

            let controlCapabilities = deviceModelsMetadata.categories[productCategory].controlCapabilities
            for (let capabilityName in controlCapabilities) {
                const attrs = controlCapabilities[capabilityName]?.attributes;
            if (!attrs || typeof attrs !== 'object') continue;
            for (let index in attrs) {
                    if (attrs[index]?.valueType instanceof Object &&
                        Array.isArray(attrs[index]?.valueType)
                    ) {
                        controlCapabilitySelectedValueType[attrs[index]?.key] = ""
                    }
                }
            }

            setControlCapabilitySelectedValueType(controlCapabilitySelectedValueType)
        }
    }, [])

    useEffect(() => {
        let ctrlCapabilitiesAttributes = {}

        if (editDeviceModel || isDeviceModelAddedUsingFileUpload) {
            const controlCapabilities = Array.isArray(deviceModelData?.controlCapabilities)
                ? deviceModelData.controlCapabilities
                : [];
            for (let capability of controlCapabilities) {
                let capabilityName = capability['capability']
                if( (deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities) && !([capabilityName] in deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities)){
                    ctrlCapabilitiesAttributes[[capabilityName]] = isJsonStringifySuccessful(capability, {formatted: true})
                }
                else{
                    ctrlCapabilitiesAttributes[capabilityName] = {}
                    let attributes = capability['attributes'];

                    ctrlCapabilitiesAttributes[capabilityName]["attributes"] = {}

                    for (let index in attributes) {
                        ctrlCapabilitiesAttributes[capabilityName]["attributes"][index] = {}
                        ctrlCapabilitiesAttributes[capabilityName]["attributes"][index]['key'] = attributes[index]['key']

                        if (attributes[index].hasOwnProperty('values')) {
                            ctrlCapabilitiesAttributes[capabilityName]["attributes"][index]['values'] = JSON.parse(JSON.stringify(attributes[index]['values']))
                            ctrlCapabilitiesAttributes[capabilityName]["attributes"][index]['customValue'] = ''

                            controlCapabilitySelectedValueType[attributes[index]['key']] = "values"
                        }
                        if (attributes[index].hasOwnProperty('intValues')) {
                            ctrlCapabilitiesAttributes[capabilityName]["attributes"][index]['intValues'] = JSON.parse(JSON.stringify(attributes[index]['intValues']))
                            ctrlCapabilitiesAttributes[capabilityName]["attributes"][index]['customValue'] = ''

                            controlCapabilitySelectedValueType[attributes[index]['key']] = "intValues"
                        }
                        if (attributes[index].hasOwnProperty('range')) {
                            ctrlCapabilitiesAttributes[capabilityName]["attributes"][index]['range'] = JSON.parse(JSON.stringify(attributes[index]["range"]))
                        
                            controlCapabilitySelectedValueType[attributes[index]['key']] = "range"
                        }
                    }
                }
            }
        }

        if (deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities) {
            let controlCapabilities = deviceModelsMetadata.categories[productCategory].controlCapabilities

            for (let capabilityName in controlCapabilities) {

                if (ctrlCapabilitiesAttributes.hasOwnProperty(capabilityName)) {
                    continue;
                }

                ctrlCapabilitiesAttributes[capabilityName] = {}
                let attributes = controlCapabilities[capabilityName]?.['attributes'];

                ctrlCapabilitiesAttributes[capabilityName]["attributes"] = {}

                if (!attributes || typeof attributes !== 'object') continue;
                for (let index in attributes) {
                    ctrlCapabilitiesAttributes[capabilityName]["attributes"][index] = {}
                    ctrlCapabilitiesAttributes[capabilityName]["attributes"][index]['key'] = attributes[index]['key'] ? attributes[index]['key'] : ""

                    if (attributes[index].valueType==='values') {
                        ctrlCapabilitiesAttributes[capabilityName]["attributes"][index]['values'] = []
                    }

                    if (attributes[index].valueType==='intValues') {
                        ctrlCapabilitiesAttributes[capabilityName]["attributes"][index]['intValues'] = []
                    }

                    if (attributes[index].valueType==='range') {
                        ctrlCapabilitiesAttributes[capabilityName]["attributes"][index]['range'] = JSON.parse(JSON.stringify(attributes[index]["range"]))
                    }

                    if (attributes[index].valueType instanceof Object && Array.isArray(attributes[index].valueType)) {

                        for (let valueIndex in attributes[index].valueType) {
                            let valueName = attributes[index].valueType[valueIndex]

                            if (valueName==='values') {
                                ctrlCapabilitiesAttributes[capabilityName]["attributes"][index][valueName] = []
                            }
        
                            if (valueName==='intValues') {
                                ctrlCapabilitiesAttributes[capabilityName]["attributes"][index][valueName] = []
                            }
        
                            if (valueName==='range') {
                                ctrlCapabilitiesAttributes[capabilityName]["attributes"][index][valueName] = JSON.parse(JSON.stringify(attributes[index][valueName]))
                            }
                        }
                    }
                }
            }
        }
        setControlCapabilitiesAttributes(ctrlCapabilitiesAttributes)

    }, [productCategory, editDeviceModel, isDeviceModelAddedUsingFileUpload])

    useEffect(() => {
        if (editDeviceModel || isDeviceModelAddedUsingFileUpload) {
            draftCustomCapabilityNamesRef.current = new Set();
            const controlCapabilities = Array.isArray(deviceModelData?.controlCapabilities)
                ? deviceModelData.controlCapabilities
                : [];
            let addedControlCapabilitiesList = [];
            const seenFromApi = new Set();
            for (let controlCapability of controlCapabilities) {
                const cap = controlCapability?.capability;
                if (cap == null || cap === "") continue;
                if (seenFromApi.has(cap)) continue;
                seenFromApi.add(cap);
                addedControlCapabilitiesList.push(cap);
            }

            setAddedControlCapabilities(
                orderControlCapabilitiesForPayload(
                    dedupeCapabilityNamesPreserveOrder(addedControlCapabilitiesList),
                )
            )
            setControlCapabiltiesList((prevState) => {
                // return prevState.filter((capabilityName) => !(addedControlCapabilitiesList.includes(capabilityName))).sort()
                return prevState.filter((capabilityName) => !(addedControlCapabilitiesList.includes(capabilityName)))
            })
        }
        else {
            // setControlCapabiltiesList(deviceModelsMetadata["categories"][productCategory] ? Object.keys(deviceModelsMetadata["categories"][productCategory]["controlCapabilities"]) : [])
            setControlCapabiltiesList(() => {
            const capabilities =
                deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities
                ? Object.keys(
                    deviceModelsMetadata.categories[productCategory].controlCapabilities
                    ).sort()
                : [];

            return [
                customCapabilityName,
                ...capabilities.filter(cap => cap !== customCapabilityName),
            ];
            });
            draftCustomCapabilityNamesRef.current = new Set();
            setAddedControlCapabilities([])
        }

    }, [productCategory, editDeviceModel, isDeviceModelAddedUsingFileUpload])

    useEffect(() => {
        let nextSelectedCapability = ""
        if (controlCapabiltiesList && controlCapabiltiesList.length > 0) {
            nextSelectedCapability = controlCapabiltiesList[0]
        }
        setSelectedControlCapability(nextSelectedCapability)
    }, [controlCapabiltiesList])


    useEffect(() => {
        if (!triggerAddNewDeviceModel) {
            return;
        }
        const caps = dedupeCapabilityNamesPreserveOrder(addedControlCapabilitiesRef.current);
        const attrs = controlCapabilitiesAttributesRef.current;
        const errs = errorsRef.current;
        const orderedForPayload = orderControlCapabilitiesForPayload(caps);
        const controlCapabilitiesData = getControlCapabilityData(orderedForPayload, attrs);
        addDeviceControlCapabilties(controlCapabilitiesData);
        draftCustomCapabilityNamesRef.current = new Set();
        setAddedControlCapabilities(orderedForPayload);
        areAllCustomJsonValidCheck(areAllCustomJsonValid(errs), "control");
    }, [triggerAddNewDeviceModel])

    function handleSelectControlCapability(selectedCapability) {
        setSelectedControlCapability(selectedCapability)
    }

    function handleAddControlCapability(addedCapabilityName) {        
        if(addedCapabilityName === customCapabilityName){
            const nextName = computeNextCustomCapabilityInstanceName(
                addedControlCapabilitiesRef.current,
                Object.keys(controlCapabilitiesAttributesRef.current),
            );

            const emptyJsonStr = isJsonStringifySuccessful({}, { formatted: true });

            draftCustomCapabilityNamesRef.current = new Set(draftCustomCapabilityNamesRef.current).add(
                nextName,
            );

            setControlCapabilitiesAttributes((prev) => ({
                ...prev,
                [nextName]: emptyJsonStr,
            }));

            setAddedControlCapabilities((prevState) =>
                orderCapabilitiesWhileEditing(
                    dedupeCapabilityNamesPreserveOrder([...prevState, nextName]),
                )
            )
        }
        else{
            setAddedControlCapabilities((prevState) =>
                orderCapabilitiesWhileEditing(
                    dedupeCapabilityNamesPreserveOrder([...prevState, addedCapabilityName]),
                )
            )

            setControlCapabiltiesList((prevState) => {
                // return prevState.filter((capabilityName) => capabilityName!==selectedControlCapability).sort()
                return prevState.filter((capabilityName) => capabilityName!==selectedControlCapability)
            })
        }
    }

    function handleRemoveControlCapability(removedCapabilityName) {
        // handle custom removal logic
        if ( (isCustomCapability(removedCapabilityName)) || !((deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities) && ([removedCapabilityName] in deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities)) ) {
            draftCustomCapabilityNamesRef.current.delete(removedCapabilityName);
            setControlCapabilitiesAttributes((prev) => {
                const { [removedCapabilityName]: _, ...rest } = prev;
                setAddedControlCapabilities((list) =>
                    orderCapabilitiesWhileEditing(
                        dedupeCapabilityNamesPreserveOrder(
                            list.filter((capabilityName) => capabilityName !== removedCapabilityName),
                        ),
                    )
                );
                return rest;
            });

            setErrors(prev => {
                const { [removedCapabilityName]: _, ...rest } = prev;
                return rest;
            });

            setTouched(prev => {
                const { [removedCapabilityName]: _, ...rest } = prev;
                return rest;
            });

        } else {
            // handle backend capability removal
            setAddedControlCapabilities((prevState) =>
                orderCapabilitiesWhileEditing(
                    dedupeCapabilityNamesPreserveOrder(
                        prevState.filter((capabilityName) => capabilityName !== removedCapabilityName),
                    ),
                )
            )

            setControlCapabiltiesList((prevState) => {
                return [...prevState, removedCapabilityName]
            })
        }
    }

    function handleControlCapabilitiesAttributes(capabilityAttributes) {
        setControlCapabilitiesAttributes(capabilityAttributes)
    }

    function onChangeAttributeKeyName(capabilityName, attributeIndex, keyName) {
        let capabilityAttributes = { ...controlCapabilitiesAttributes }
        capabilityAttributes[capabilityName]["attributes"][attributeIndex]["key"] = keyName

        handleControlCapabilitiesAttributes(capabilityAttributes)
    }

    function onChangeAttributeValues(capabilityName, attributeIndex, values) {
        let capabilityAttributes = { ...controlCapabilitiesAttributes }
        let customValue = capabilityAttributes[capabilityName]["attributes"][attributeIndex]['customValue']
        if (customValue && customValue.length!==0) {
            values.push(customValue)
        }

        capabilityAttributes[capabilityName]["attributes"][attributeIndex]["values"] = values

        handleControlCapabilitiesAttributes(capabilityAttributes)
    }

    function onChangeAttributeIntValues(capabilityName, attributeIndex, values) {
        let capabilityAttributes = { ...controlCapabilitiesAttributes }

        capabilityAttributes[capabilityName]["attributes"][attributeIndex]["intValues"] = values

        handleControlCapabilitiesAttributes(capabilityAttributes)
    }

    function onChangeAttributeCustomValue(capabilityName, attributeIndex, value) {
        let capabilityAttributes = { ...controlCapabilitiesAttributes }

        capabilityAttributes[capabilityName]["attributes"][attributeIndex]["customValue"] = value

        handleControlCapabilitiesAttributes(capabilityAttributes)
    }

    function onChangeAttributeRangeValue(capabilityName, attributeIndex, rangeName, rangeValueIndex, value) {
        let capabilityAttributes = { ...controlCapabilitiesAttributes }
        capabilityAttributes[capabilityName]["attributes"][attributeIndex]['range'][rangeName][rangeValueIndex]['value'] = value

        handleControlCapabilitiesAttributes(capabilityAttributes)
    }

    function onChangeAttributeRangeUnit(capabilityName, attributeIndex, rangeName, rangeValueIndex, uintName) {
        let capabilityAttributes = { ...controlCapabilitiesAttributes }
        capabilityAttributes[capabilityName]["attributes"][attributeIndex]['range'][rangeName][rangeValueIndex]['unit'] = uintName

        handleControlCapabilitiesAttributes(capabilityAttributes)
    }

    function onChangeCapabilityCustomJson(capabilityName, value) {
        const nextAttrs = { ...controlCapabilitiesAttributes, [capabilityName]: value };

        setControlCapabilitiesAttributes(nextAttrs);

        const error = validateJSONData(value);
        setErrors((prev) => ({ ...prev, [capabilityName]: error }));
        if (error && error !== "") {
            setTouched((prev) => ({ ...prev, [capabilityName]: "true" }));
        }
    }

    function getControlCapabilitySupportedDeviceTypes(capabilityName) {
        let capability = deviceModelsMetadata['categories'][productCategory]['controlCapabilities'][capabilityName];

        if (capability.hasOwnProperty('supportedDeviceTypes')) {
            let supportedDeviceTypes = capability['supportedDeviceTypes']

            return supportedDeviceTypes.filter((deviceType) => {
                return channelAndSwitchDeviceTypes.includes(deviceType)!==false
            })
        }
        else {
            return channelAndSwitchDeviceTypes;
        }
    }

    /** isJsonParseSuccessful only accepts strings; loaded values may be objects — normalize for payload. */
    function parseCustomCapabilityJsonPayload(raw) {
        if (raw === undefined || raw === null) return false;
        if (typeof raw === "string") {
            const parsed = isJsonParseSuccessful(raw, { logError: false });
            return parsed !== false ? parsed : false;
        }
        if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
            try {
                return JSON.parse(JSON.stringify(raw));
            } catch {
                return false;
            }
        }
        return false;
    }

    function getControlCapabilityData(
        orderedCapabilityNames = addedControlCapabilities,
        capabilitiesAttributes = controlCapabilitiesAttributes,
    ) {
        let controlCapabilities = []
        let uniqueKeys = {};
        for (let capabilityName of orderedCapabilityNames) {
            if( (isCustomCapability(capabilityName)) || !((deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities) && ([capabilityName] in deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities)) ){
                const parsedCustom = parseCustomCapabilityJsonPayload(capabilitiesAttributes[capabilityName]);
                if (parsedCustom !== false) {
                    controlCapabilities.push(parsedCustom);
                }
            }
            else{
                let capabilityData = {}
                capabilityData['capability'] = capabilityName
                capabilityData['attributes'] = []

                if (productCategory===customConfig.deviceCategorySWITCHES) {
                    capabilityData['devices'] = getControlCapabilitySupportedDeviceTypes(capabilityName)
                }

                let attributes = capabilitiesAttributes[capabilityName]['attributes'];
                for (let index in attributes) {
                    let attributeData = {}
                    attributeData['key'] = attributes[index]['key']

                    // if (uniqueKeys.hasOwnProperty(attributes[index]['key'])) {
                    //     controlCapabilities = {}
                    //     controlCapabilities['error'] = 'Control Capabilities Cannot Have the Same Key Name'
                    //     return controlCapabilities;
                    // }

                    uniqueKeys[attributes[index]['key']] = "";

                    if (attributes[index].hasOwnProperty('values')) {
                        attributeData['values'] = attributes[index]['values']

                        let customValue = attributes[index]['customValue']
                        if (customValue && customValue.length!==0 && !isNaN(customValue)) {
                            attributeData['values'].push(customValue)
                        }
                    }
                    else if (attributes[index].hasOwnProperty('intValues')) {
                        attributeData['intValues'] = attributes[index]['intValues']

                        let customValue = attributes[index]['customValue']
                        if (customValue && customValue.length!==0 && !isNaN(customValue)) {
                            attributeData['intValues'].push(parseInt(customValue))
                        }
                    }
                    else if (attributes[index].hasOwnProperty('range')) {
                        attributeData['range'] = attributes[index]['range']
                    }

                    capabilityData['attributes'].push(attributeData)
                }

                controlCapabilities.push(capabilityData)
            }
        }

        return controlCapabilities;
    }

    function onChangeControlCapabilitySelectedValueType(capabilityName, key, valueTypeSelected) {

        let previousSelectedValueType = controlCapabilitySelectedValueType

        setControlCapabilitySelectedValueType((prevState) => {
            return {
                ...prevState,
                [key]: valueTypeSelected
            }
        })

        let capabilityAttributes = { ...controlCapabilitiesAttributes }

        // get information from metdata
        let metadataAttributes = deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities?.[capabilityName]?.attributes
        if (!metadataAttributes || typeof capabilityAttributes[capabilityName]?.attributes !== 'object') return;
        for (let index in metadataAttributes) {

            if (metadataAttributes[index].valueType instanceof Object && Array.isArray(metadataAttributes[index].valueType)
            ) {
                for (let attr in capabilityAttributes[capabilityName]["attributes"][index]) {

                    // delete those unnecessary attributes
                    if (!['key', valueTypeSelected].includes(attr)) {
                        delete capabilityAttributes[capabilityName]["attributes"][index][attr];
                    }
                }
            }
        }

        // add those attributes which are not part of capability attributes that are deleted earlier
        for (let index in capabilityAttributes[capabilityName]["attributes"]) {
            if (!capabilityAttributes[capabilityName]["attributes"][index].hasOwnProperty(valueTypeSelected)) {

                for (let metaAttrIndex in metadataAttributes) {

                    if (metadataAttributes[metaAttrIndex].hasOwnProperty(valueTypeSelected)) {

                        capabilityAttributes[capabilityName]["attributes"][index][valueTypeSelected] =
                            metadataAttributes[metaAttrIndex][valueTypeSelected]

                        break
                    }
                }
            }
        }
        setControlCapabilitiesAttributes(capabilityAttributes)
    }

    return (
        <Fragment>
            <Grid2 container spacing={2} sx={{ alignItems: "flex-end", mt: 1.5, mb: 2 }}>
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <InputLabel shrink={false} sx={{ position: "static", transform: "none", mb: 0.75, fontSize: "0.875rem", color: "rgba(0, 0, 0, 0.6)" }}>Select Capability</InputLabel>
                    <Select
                        value={selectedControlCapability}
                        onChange={(e) => handleSelectControlCapability(e.target.value)}
                        displayEmpty
                        fullWidth
                        size="small"
                        input={<OutlinedInput />}
                        inputProps={{ 'aria-label': 'Without label' }}
                        sx={selectInputSx}
                        MenuProps={selectMenuProps}
                    >
                        {/* {
                            controlCapabiltiesList?.sort().map((capabilityName) => {
                                return <MenuItem key={capabilityName} value={capabilityName}>{convertCapabilityName(capabilityName)}</MenuItem>
                            })
                        } */}
                        {
                            controlCapabiltiesList?.map((capabilityName) => {
                                return <MenuItem key={capabilityName} value={capabilityName}>{convertCapabilityName(capabilityName)}</MenuItem>
                            })
                        }
                    </Select>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box sx={{ height: "1.25rem", mb: 0.75 }} aria-hidden />
                    <AppButton
                        variant="primary"
                        size="medium"
                        onClick={() => handleAddControlCapability(selectedControlCapability)}
                        disabled={controlCapabiltiesList && controlCapabiltiesList.length === 0}
                    >
                        Add Capability
                    </AppButton>
                </Grid2>
            </Grid2>
            {
                dedupeCapabilityNamesPreserveOrder(addedControlCapabilities).map((capabilityName) => {
                    const isCustom = isCustomCapability(capabilityName) ||
                        !(deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities &&
                            capabilityName in deviceModelsMetadata.categories[productCategory].controlCapabilities);
                    return <Fragment key={productCategory + '_' + capabilityName}>
                        <Card variant="outlined" sx={capabilityCardSx} key={capabilityName}>
                            {(isCustomCapability(capabilityName)
                                || !((deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities) && ([capabilityName] in deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities))
                            ) ?
                            <CardContent>
                                <Grid2 container alignItems="flex-start" spacing={1}>
                                    <Grid2 size={10} sx={{margin: 'auto'}}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {CONFIG?.customJson?.label}
                                        </Typography>
                                    </Grid2>

                                    <Grid2 size={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                        <IconButton
                                        aria-label="delete"
                                        onClick={() => handleRemoveControlCapability(capabilityName)}
                                        sx={{ p: 0.5 }}
                                        >
                                        <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Grid2>

                                    <FormInput
                                        id={capabilityName}
                                        name={capabilityName}
                                        type="textarea"
                                        multiline
                                        rows={CONFIG?.customJson?.rows}
                                        fullWidth
                                        value={controlCapabilitiesAttributes[capabilityName]}
                                        onChange={(e) => onChangeCapabilityCustomJson(capabilityName, e.target.value)}
                                        placeholder={CONFIG?.customJson?.placeholder}
                                        error={!!errors?.[capabilityName]}
                                        touched={touched[capabilityName]}
                                        helperText={errors?.[capabilityName] || CONFIG?.customJson?.helpText}
                                        sx={{
                                        "& .MuiOutlinedInput-input": {
                                            padding: "2px 6pxpx !important",
                                            height: "130px !important",
                                        },
                                        "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderWidth: `2px solid !important`,
                                        },
                                        }}
                                        slotProps={{
                                            htmlInput: {
                                                maxLength: 10000,
                                            },
                                        }}
                                    />
                                </Grid2>
                            </CardContent>
                            :
                            <CardContent>
                                {isCustom ? (
                                    <Grid2 container spacing={1} alignItems="flex-start">
                                        <Grid2 size={10} sx={{ margin: "auto" }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                {CUSTOM_JSON_CONFIG.label}
                                            </Typography>
                                        </Grid2>
                                        <Grid2 size={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                            <IconButton
                                                aria-label="delete"
                                                onClick={() => handleRemoveControlCapability(capabilityName)}
                                                sx={{ p: 0.5 }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Grid2>
                                        <Grid2 size={12}>
                                            <FormInput
                                                id={capabilityName}
                                                name={capabilityName}
                                                type="textarea"
                                                multiline
                                                rows={CUSTOM_JSON_CONFIG.rows}
                                                fullWidth
                                                value={typeof controlCapabilitiesAttributes[capabilityName] === "string" ? controlCapabilitiesAttributes[capabilityName] : ""}
                                                onChange={(e) => onChangeCapabilityCustomJson(capabilityName, e.target.value)}
                                                placeholder={CUSTOM_JSON_CONFIG.placeholder}
                                                error={!!errors?.[capabilityName]}
                                                touched={touched[capabilityName]}
                                                helperText={errors?.[capabilityName] || CUSTOM_JSON_CONFIG.helpText}
                                                sx={{
                                                    width: "100%",
                                                    minWidth: "100%",
                                                    "& .MuiOutlinedInput-root": {
                                                        minHeight: 220,
                                                        height: "auto",
                                                        alignItems: "flex-start",
                                                        "& .MuiOutlinedInput-notchedOutline": { border: "1px solid rgba(0, 0, 0, 0.42) !important" },
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        minHeight: "200px !important",
                                                        height: "auto !important",
                                                        boxSizing: "border-box",
                                                        padding: "8.5px 14px !important",
                                                    },
                                                }}
                                            />
                                        </Grid2>
                                    </Grid2>
                                ) : (
                                    <>
                                <Grid2 container spacing={1} sx={{ mb: 1 }}>
                                    <Grid2 size={{ xs: 10, sm: 11 }}>
                                        <Typography style={{ fontWeight: 600 }}>{convertCapabilityName(capabilityName)}</Typography>
                                    </Grid2>
                                    <Grid2 size={{ xs: 2, sm: 1 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                        <IconButton aria-label="delete" onClick={(e) => {
                                            handleRemoveControlCapability(capabilityName)
                                        }} sx={deleteCapabilityCardButtonSx}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Grid2>
                                </Grid2>
                                {deviceModelsMetadata?.categories?.[productCategory]?.controlCapabilities?.[capabilityName]?.attributes?.map((element, index) => {
                                    const capabilityAttrs = controlCapabilitiesAttributes?.[capabilityName]?.attributes?.[index] ?? {};
                                    const selectedValues = Array.isArray(capabilityAttrs.values) ? capabilityAttrs.values : [];
                                    const selectedIntValues = Array.isArray(capabilityAttrs.intValues) ? capabilityAttrs.intValues : [];
                                    return <Grid2 key={capabilityName + '_' + index} container alignItems="flex-start" spacing={1} sx={{ mb: 2 }}>
                                        <Grid2 size={{ xs: 12, sm: 1 }} sx={{ pt: { xs: 0.5, sm: 0 } }}>
                                            <Typography style={{ fontSize: "0.85rem", fontWeight: 400 }}>Attribute</Typography>
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, sm: element.valueType === 'range' ? 11 : 2 }} sx={element.valueType === 'range' ? {} : { ...attributeKeySx }}>
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                                <Box sx={element.valueType === 'range' ? { display: "flex", gap: 1, flexWrap: "wrap" } : {}}>
                                                    <Box sx={element.valueType === 'range' ? { flex: "0 1 calc(50% - 4px)", minWidth: { xs: 0, sm: 80 } } : {}}>
                                                        <FormInput
                                                            placeholder="Enter Key Name"
                                                            value={controlCapabilitiesAttributes[capabilityName]?.attributes?.[index]?.key}
                                                            disabled={true}
                                                            onChange={(e) => onChangeAttributeKeyName(capabilityName, index, e.target.value)}
                                                            sx={focusOutlineSx}
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </Box>
                                                </Box>
                                                {element.valueType === 'range' && (
                                                <Box sx={{ marginTop: 2 }}>
                                                {
                                                    Object.keys(element.range || {}).map((rangeName) => (
                                                        <Box key={rangeName} sx={{ mb: 2 }}>
                                                            <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, mb: 0.5, display: "block" }}>Range {rangeName}</Typography>
                                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                                {
                                                                    (element.range?.[rangeName] || []).map((rangeValue, rangeValueIndex) => {
                                                                        const hasUnit = rangeValue.hasOwnProperty('unit');
                                                                        const singleInput = !hasUnit;
                                                                        return (
                                                                        <Box key={rangeValueIndex} className="range-value-unit-row" sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                                                                            <Box sx={{ flex: singleInput ? "0 1 50%" : "0 1 calc(50% - 4px)", minWidth: { xs: 0, sm: 80 } }}>
                                                                                <FormInput
                                                                                    placeholder="Enter Value"
                                                                                    value={controlCapabilitiesAttributes?.[capabilityName]?.attributes?.[index]?.range?.[rangeName]?.[rangeValueIndex]?.value ?? ""}
                                                                                    onChange={(e) => onChangeAttributeRangeValue(capabilityName, index, rangeName, rangeValueIndex, e.target.value)}
                                                                                    disabled={element.editable===false}
                                                                                    sx={focusOutlineSx}
                                                                                    InputLabelProps={{ shrink: true }}
                                                                                />
                                                                            </Box>
                                                                            {
                                                                                rangeValue.hasOwnProperty('unit') && (
                                                                                    <Box sx={{ flex: "0 1 calc(50% - 4px)", minWidth: { xs: 0, sm: 80 } }}>
                                                                                        <FormInput
                                                                                            placeholder="Enter Unit"
                                                                                            value={controlCapabilitiesAttributes?.[capabilityName]?.attributes?.[index]?.range?.[rangeName]?.[rangeValueIndex]?.unit ?? ""}
                                                                                            onChange={(e) => onChangeAttributeRangeUnit(capabilityName, index, rangeName, rangeValueIndex, e.target.value)}
                                                                                            disabled={element.editable===false}
                                                                                            sx={focusOutlineSx}
                                                                                            InputLabelProps={{ shrink: true }}
                                                                                        />
                                                                                    </Box>
                                                                                )
                                                                            }
                                                                        </Box>
                                                                    );
                                                                    })
                                                                }
                                                            </Box>
                                                        </Box>
                                                    ))
                                                }
                                                </Box>
                                                )}
                                            </Box>
                                        </Grid2>
                                        {
                                            element.valueType === 'values' &&
                                            <Fragment>
                                                <Grid2 size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
                                                    <FormControl variant="outlined" sx={attributeValuesSx}>
                                                        <InputLabel id="select-outlined-label-values">Values</InputLabel>
                                                        <Select
                                                            labelId="select-outlined-label-values"
                                                            id="select-outlined-values-id"
                                                            value={controlCapabilitiesAttributes[capabilityName]?.attributes?.[index]?.values || []}
                                                            multiple
                                                            onChange={(e) => {
                                                                onChangeAttributeValues(capabilityName, index, e.target.value)
                                                            }}
                                                            label="Values"
                                                            renderValue={(selected) => selected.join(', ')}
                                                            disabled={element.editable == false ? true : false}
                                                            sx={selectInputSx}
                                                            MenuProps={selectMenuProps}
                                                        >
                                                            {Array.isArray(element?.values) && element?.values.map((listVal) => (
                                                                <MenuItem key={listVal} value={listVal}>
                                                                    <Checkbox checked={controlCapabilitiesAttributes[capabilityName]?.attributes?.[index]?.values?.indexOf(listVal) > -1} />
                                                                    <ListItemText primary={listVal} />
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid2>
                                                {
                                                    element.addCustomValue===true && <Grid2 size={{ xs: 12, sm: 4 }}>
                                                        <FormInput
                                                            placeholder="Enter New Value (if any)"
                                                            onChange={(e) => onChangeAttributeCustomValue(capabilityName, index, e.target.value)}
                                                            sx={{ ...focusOutlineSx, ...customValueKeySx }}
                                                            InputLabelProps={{ shrink: true }}
                                                            value={controlCapabilitiesAttributes[capabilityName]?.attributes?.[index]?.customValue}
                                                            type="text"
                                                        />
                                                    </Grid2>
                                                }
                                            </Fragment>
                                        }
                                        {
                                            element.valueType === 'intValues' &&
                                            <Fragment>
                                                <Grid2 size={{ xs: 12, sm: 4 }}>
                                                    <FormControl variant="outlined" sx={attributeValuesSx}>
                                                        <InputLabel id="select-outlined-label-intValues">Values</InputLabel>
                                                        <Select
                                                            labelId="select-outlined-label-intValues"
                                                            id="select-outlined-intValues-id"
                                                            value={controlCapabilitiesAttributes[capabilityName]?.attributes?.[index]?.intValues ?? []}
                                                            multiple
                                                            onChange={(e) => {
                                                                onChangeAttributeIntValues(capabilityName, index, e.target.value)
                                                            }}
                                                            label="Values"
                                                            renderValue={(selected) => selected.join(', ')}
                                                            disabled={element.editable == false ? true : false}
                                                            sx={selectInputSx}
                                                            MenuProps={selectMenuProps}
                                                        >
                                                            {Array.isArray(element?.intValues) && element?.intValues.map((value) => (
                                                                <MenuItem key={value} value={value}>
                                                                    <Checkbox checked={controlCapabilitiesAttributes[capabilityName]?.attributes?.[index]?.intValues?.indexOf(value) > -1} />
                                                                    <ListItemText primary={value} />
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid2>
                                                {
                                                    element.addCustomValue===true && <Grid2 size={{ xs: 12, sm: 4 }}>
                                                        <FormInput
                                                            placeholder="Enter New Value (if any)"
                                                            sx={{ ...focusOutlineSx, ...customValueKeySx }}
                                                            value={controlCapabilitiesAttributes[capabilityName]?.attributes?.[index]?.customValue}
                                                            type="text"
                                                            onChange={(e) => {
                                                                onChangeAttributeCustomValue(capabilityName, index, e.target.value)
                                                            }}
                                                        />
                                                    </Grid2>
                                                }
                                            </Fragment>
                                        }
                                        {
                                            (element.valueType instanceof Object && Array.isArray(element.valueType)) &&
                                            <Fragment>
                                                <Grid2 size={{ xs: 12, sm: 4 }}>
                                                    <FormControl variant="outlined" sx={attributeValuesSx}>
                                                        <InputLabel id="select-outlined-label-values-types-array">Value Type</InputLabel>
                                                        <Select
                                                            labelId="select-outlined-label-values-types-array"
                                                            id="select-outlined-values-id-values-types-array"
                                                            value={controlCapabilitySelectedValueType?.[element?.key]}
                                                            onChange={(e) => {
                                                                onChangeControlCapabilitySelectedValueType(capabilityName, element.key, e.target.value)
                                                            }}
                                                            label="Value Type"
                                                            sx={selectInputSx}
                                                            MenuProps={selectMenuProps}
                                                        >
                                                            {Array.isArray(element?.valueType) && element?.valueType.map((valueTypeElement, index) => (
                                                                <MenuItem key={valueTypeElement} value={valueTypeElement}>
                                                                    <ListItemText primary={valueTypeElement} />
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid2>
                                                {
                                                    controlCapabilitySelectedValueType[element.key]==='intValues' &&
                                                    <Grid2 size={{ xs: 12, sm: 4 }} sx={{ ml: { xs: 0, sm: 2.5 } }}>
                                                        <FormControl variant="outlined" sx={attributeValuesSx}>
                                                            <InputLabel id="select-outlined-label-intValues">Values</InputLabel>
                                                            <Select
                                                                labelId="select-outlined-label-intValues"
                                                                id="select-outlined-intValues-id"
                                                                value={controlCapabilitiesAttributes[capabilityName]?.attributes?.[index]?.intValues ?? []}
                                                                multiple
                                                                onChange={(e) => {
                                                                    onChangeAttributeIntValues(capabilityName, index, e.target.value)
                                                                }}
                                                                label="Values"
                                                                renderValue={(selected) => selected.join(', ')}
                                                                disabled={element.editable == false ? true : false}
                                                                sx={selectInputSx}
                                                                MenuProps={selectMenuProps}
                                                            >
                                                                {Array.isArray(element?.intValues) && element?.intValues.map((value) => (
                                                                    <MenuItem key={value} value={value}>
                                                                        <Checkbox checked={controlCapabilitiesAttributes[capabilityName]?.attributes?.[index]?.intValues?.indexOf(value) > -1} />
                                                                        <ListItemText primary={value} />
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid2>
                                                }
                                                {
                                                    controlCapabilitySelectedValueType[element.key]==='range' && (
                                                        <Stack direction="column" spacing={1} sx={{ ml: { sm: 2 }, marginTop: 2, width: "100%" }}>
                                                            {
                                                                Object.keys(element.range || {}).map((rangeName) => (
                                                                    <Box key={rangeName} sx={{ mb: 2 }}>
                                                                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, mb: 0.5, display: "block" }}>Range {rangeName}</Typography>
                                                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                                            {
                                                                                (element.range?.[rangeName] || []).map((rangeValue, rangeValueIndex) => {
                                                                                    const hasUnit = rangeValue.hasOwnProperty('unit');
                                                                                    const singleInput = !hasUnit;
                                                                                    return (
                                                                                    <Box key={rangeValueIndex} sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                                                                                        <Box sx={{ minWidth: { xs: 0, sm: 80 }, flex: singleInput ? "0 1 50%" : "1 1 80px" }}>
                                                                                            <FormInput
                                                                                                placeholder="Enter Value"
                                                                                                value={controlCapabilitiesAttributes?.[capabilityName]?.attributes?.[index]?.range?.[rangeName]?.[rangeValueIndex]?.value ?? ""}
                                                                                                onChange={(e) => onChangeAttributeRangeValue(capabilityName, index, rangeName, rangeValueIndex, e.target.value)}
                                                                                                disabled={element.editable===false}
                                                                                                sx={focusOutlineSx}
                                                                                                InputLabelProps={{ shrink: true }}
                                                                                            />
                                                                                        </Box>
                                                                                        {
                                                                                            rangeValue.hasOwnProperty('unit') && (
                                                                                                <Box sx={{ minWidth: { xs: 0, sm: 100 }, flex: "1 1 100px" }}>
                                                                                                    <FormInput
                                                                                                        placeholder="Enter Unit"
                                                                                                        value={controlCapabilitiesAttributes?.[capabilityName]?.attributes?.[index]?.range?.[rangeName]?.[rangeValueIndex]?.unit ?? ""}
                                                                                                        onChange={(e) => onChangeAttributeRangeUnit(capabilityName, index, rangeName, rangeValueIndex, e.target.value)}
                                                                                                        disabled={element.editable===false}
                                                                                                        sx={focusOutlineSx}
                                                                                                        InputLabelProps={{ shrink: true }}
                                                                                                    />
                                                                                                </Box>
                                                                                            )
                                                                                        }
                                                                                    </Box>
                                                                                    );
                                                                                })
                                                                            }
                                                                        </Box>
                                                                    </Box>
                                                                ))
                                                            }
                                                        </Stack>
                                                    )
                                                }
                                            </Fragment>
                                        }
                                    </Grid2>
                                })}
                                    </>
                                )}
                            </CardContent>
                            }
                        </Card>
                    </Fragment>
                })
            }
        </Fragment >
    )
}

const mapStateToProps = (state) => ({
    ModelData: state.getModel
});

export default connect(mapStateToProps)(DeviceModelControlCapability);